import angular from 'angular';
import { RootScopeEvents } from '../root-scope-types';
import './live-announcer.scss';
export const TRANSLATION_KEYS = {
    callStarted: 'video-chat.announcement.call-started',
    callStartedAsPatient: 'video-chat.announcement.call-started-as-patient',
    callStartedAsDoctor: 'video-chat.announcement.call-started-as-doctor',
    callEnded: 'video-chat.announcement.call-ended',
};
export class LiveAnnouncerService {
    constructor($rootScope) {
        this.$rootScope = $rootScope;
        this.polite = '';
        this.assertive = '';
    }
    announce(message, politeness = 'polite') {
        if (politeness === 'assertive') {
            this.assertive = message;
            this.polite = '';
        }
        else {
            this.polite = message;
            this.assertive = '';
        }
        // The triggering events are emitted via `$rootScope.$emit` from outside
        // a digest (e.g. after `await` in `VideoChatRealtimeComponent.join`/
        // `stop`), so we have to schedule one ourselves to flush the bound
        // template into the DOM — we do it synchronously (no timeout) so the
        // live-region update happens before any subsequent route change.
        const phase = this.$rootScope.$$phase;
        if (phase == null) {
            this.$rootScope.$applyAsync();
        }
    }
    clear() {
        this.polite = '';
        this.assertive = '';
    }
}
export const getCallStartedTranslationKey = (context) => {
    if (context?.isPatient() === true) {
        return TRANSLATION_KEYS.callStartedAsPatient;
    }
    if (context?.isDoctor() === true) {
        return TRANSLATION_KEYS.callStartedAsDoctor;
    }
    return TRANSLATION_KEYS.callStarted;
};
export const registerCallLifecycleAnnouncements = ($rootScope, liveAnnouncer, $translate, pn) => {
    // Audio-only calls don't always reach `phone:started` on every device
    // (depending on media-permission negotiation), so we also listen for
    // `activeProvider:joinSession:success` — whichever fires first wins, the
    // second one is suppressed by the per-call dedupe flag.
    let alreadyAnnouncedForThisCall = false;
    const announceCallStarted = () => {
        if (alreadyAnnouncedForThisCall) {
            return;
        }
        alreadyAnnouncedForThisCall = true;
        liveAnnouncer.announce($translate.instant(getCallStartedTranslationKey(pn)), 'polite');
    };
    const offStarted = $rootScope.$on(RootScopeEvents.PHONE.STARTED, announceCallStarted);
    const offJoinedSession = $rootScope.$on(RootScopeEvents.ACTIVE_PROVIDER.JOIN_SESSION_SUCCESS, announceCallStarted);
    // "Call ended" uses an assertive region because the call-end teardown
    // typically triggers a UI/route transition that immediately competes for
    // the screen reader's attention; assertive lets the announcement preempt
    // the destination page's content.
    const offEnded = $rootScope.$on(RootScopeEvents.PHONE.END, () => {
        alreadyAnnouncedForThisCall = false;
        liveAnnouncer.announce($translate.instant(TRANSLATION_KEYS.callEnded), 'assertive');
    });
    return () => {
        offStarted();
        offJoinedSession();
        offEnded();
    };
};
export default angular
    .module('doxy.common.services.liveAnnouncer', [])
    .service('liveAnnouncer', LiveAnnouncerService)
    .component('liveAnnouncer', {
    template: `
      <div
        class="live-announcer"
        aria-live="polite"
        aria-atomic="true"
        role="status"
      >{{ $ctrl.liveAnnouncer.polite }}</div>
      <div
        class="live-announcer"
        aria-live="assertive"
        aria-atomic="true"
        role="alert"
      >{{ $ctrl.liveAnnouncer.assertive }}</div>
    `,
    controller: function (liveAnnouncer) {
        this.liveAnnouncer = liveAnnouncer;
    },
})
    .run([
    '$rootScope',
    'liveAnnouncer',
    '$translate',
    'pn',
    ($rootScope, liveAnnouncer, $translate, pn) => registerCallLifecycleAnnouncements($rootScope, liveAnnouncer, $translate, pn),
]).name;
